import { formatCents } from 'src/shared/util/money';

import { AddressModel } from 'src/models/address.model';

export interface GuestReservationSourceType {
  id: number;
  name: string;
  format: string;
  short_description: string;
  long_description: string;
  logo_url: string;
  logo_description_image_url: string;
  source_link: string;
  support_link: string;
  privacy_link: string;
  term_link: string;
}

export interface GuestReservationSource {
  guest_reservation_fetch_status: string;
  address_automatic_booking_setting: {
    automatic_booking_enabled: boolean;
    booking_end_time_no_later_than: string;
    booking_start_time_no_earlier_than: string;
    id: number;
    max_days_to_perform_job_after_checkout?: number;
    number_days_prior_to_check_in?: number;
    required_team_service: {
      description: string;
      id: number;
      key: string;
      name: string;
    }
  }
  address: AddressModel; //FE adds this to the response
  address_id: number;
  addresses?: AddressModel[]; //FE adds this for API integrations with multiple addresses
  guest_reservation_source_type: {
    format: string;
    id: number;
    logo_description_image_url: string;
    logo_url: string;
    long_description: string;
    name: string;
    privacy_link: string;
    short_description: string;
    source_link: string;
    support_link: string;
    term_link: string;
  }
  id: number;
  inactivated_by: string;
  last_sync_at: string;
  state: string;
  url: string;
}

export interface NewGuestReservationSource {
  id: number;
  guest_reservation_source_type_name: string;
  url: string;
  address_id: number;
  state: string;
  inactivated_by: string;
}

export interface GuestReservation {
  id: number;
  automatic_booking_settings: {
    bookable_service_key: string;
    is_automatic_booking_enabled: boolean;
  }[];
  guest_reservation_source_id: number;
  automatic_booking_enabled: boolean;
  booking_id: number;
  check_in_date: string;
  check_out_date: string;
  check_in_time: string;
  check_out_time: string;
  source_data: {
    guest_reservation: {
      check_in_date: string;
      check_out_date: string;
    }
    money?: ChannelMoney | null;
  }
  // Total stay price in cents. Null on a reservation nobody has priced;
  // currency is only meaningful when amount is set, and is always usd.
  amount?: number | null;
  currency?: string | null;
  // A Vrbo hold: the dates are blocked but the guest hasn't confirmed yet.
  is_tentative?: boolean;
}

// The money a connected Airbnb/Vrbo stay carries in its feed copy, in cents.
// Each value stays null until the capture has priced the stay.
export interface ChannelMoney {
  currency?: string | null;
  guest_total?: number | null;
  host_payout?: number | null;
}

interface PricedReservation {
  amount?: number | null;
  currency?: string | null;
  source_data?: { money?: ChannelMoney | null } | null;
}

// `amount` only holds a price the host typed in. A synced stay's price stays in
// its source_data, so read it from there rather than have the API copy it over.
export function reservationPriceCents(reservation: PricedReservation | null | undefined): number | null {
  return reservation?.amount ?? reservation?.source_data?.money?.guest_total ?? null;
}

export function reservationCurrency(reservation: PricedReservation | null | undefined): string | null {
  return reservation?.currency || reservation?.source_data?.money?.currency || null;
}

export function reservationHostPayoutCents(reservation: PricedReservation | null | undefined): number | null {
  return reservation?.source_data?.money?.host_payout ?? null;
}

// One typed row of a connected Airbnb/Vrbo stay's `source_data.amounts_breakdown`,
// in cents. Negative values are what the platform keeps or takes off.
export interface ChannelAmountsBreakdownItem {
  type?: string | null;
  value?: number | null;
  description?: string | null;
}

export interface ChannelBreakdownLine {
  label: string;
  cents: number;
}

// A synced stay's money, split so each group adds up to the total above it:
// guestLines to totalCents, totalCents plus platformLines to payoutCents.
export interface ChannelBreakdown {
  guestLines: ChannelBreakdownLine[];
  totalCents: number;
  platformLines: ChannelBreakdownLine[];
  payoutCents: number;
}

// The feed's own totals ride in the same list as the lines; fee_base_amount is
// Samantha's internal debit record and never a line of the stay.
const BREAKDOWN_SUMMARY_TYPES = ['total_amount', 'host_payout', 'fee_base_amount'];
// What a platform charges the host. Vrbo also lists the guest service fee and
// the taxes it remits as negatives here, after the guest paid them.
const BREAKDOWN_PLATFORM_TYPES = ['platform_fee', 'payment_processing_fee', 'guest_service_fee', 'occupancy_tax'];

const sumCents = (lines: ChannelBreakdownLine[]): number => lines.reduce((sum, line) => sum + line.cents, 0);

// Labels are built from fixed strings so each one has a translation key.
function knownPlatform(platformName?: string | null): 'Airbnb' | 'Vrbo' | 'the platform' {
  const name = (platformName ?? '').trim().toLowerCase();
  return name === 'airbnb' ? 'Airbnb' : name === 'vrbo' ? 'Vrbo' : 'the platform';
}

const KEPT_BY_LABELS = { Airbnb: 'Kept by Airbnb', Vrbo: 'Kept by Vrbo', 'the platform': 'Kept by the platform' };
const TAXES_REMITTED_LABELS = {
  Airbnb: 'Taxes remitted by Airbnb',
  Vrbo: 'Taxes remitted by Vrbo',
  'the platform': 'Taxes remitted by the platform',
};

// Airbnb and Vrbo itemize differently: Airbnb's discounts are already inside the
// nightly line and it remits taxes without listing them, while Vrbo lists both.
// So the lines are fitted to the feed's own totals, and whatever the feed leaves
// unexplained shows as one closing line rather than a total that doesn't add up.
export function channelBreakdown(
  sourceData: { amounts_breakdown?: unknown; money?: ChannelMoney | null } | null | undefined,
  platformName?: string | null
): ChannelBreakdown | null {
  const rows = Array.isArray(sourceData?.amounts_breakdown)
    ? (sourceData.amounts_breakdown as ChannelAmountsBreakdownItem[])
    : [];
  // Summed like the back end's own guest total, in case a capture stores more than one.
  const summary = (type: string) => {
    const matching = rows.filter((row) => row?.type === type && Number.isInteger(row?.value));
    return matching.length ? matching.reduce((sum, row) => sum + (row.value as number), 0) : undefined;
  };
  const totalCents = summary('total_amount') ?? sourceData?.money?.guest_total;
  const payoutCents = summary('host_payout') ?? sourceData?.money?.host_payout;
  if (!Number.isInteger(totalCents) || !Number.isInteger(payoutCents)) {
    return null;
  }
  const lines = rows
    .filter((row) => !BREAKDOWN_SUMMARY_TYPES.includes(row?.type ?? '') && Number.isInteger(row?.value) && row.value !== 0)
    .map((row) => ({ type: row.type ?? '', label: row.description || 'Other', cents: row.value as number }));
  if (!lines.length) {
    return null;
  }
  const charges = lines.filter((line) => line.cents > 0);
  const platformFees = lines.filter((line) => line.cents < 0 && BREAKDOWN_PLATFORM_TYPES.includes(line.type));
  const adjustments = lines.filter((line) => line.cents < 0 && !BREAKDOWN_PLATFORM_TYPES.includes(line.type));
  const total = totalCents as number;
  const payout = payoutCents as number;
  const adjustmentsLowerTotal = sumCents(charges) !== total && sumCents(charges) + sumCents(adjustments) === total;
  const guestLines = adjustmentsLowerTotal ? [...charges, ...adjustments] : [...charges];
  const guestGap = total - sumCents(guestLines);
  if (guestGap) {
    guestLines.push({ type: '', label: 'Other adjustments', cents: guestGap });
  }
  const platform = knownPlatform(platformName);
  const withAdjustments = adjustmentsLowerTotal ? platformFees : [...platformFees, ...adjustments];
  const platformLines = total + sumCents(withAdjustments) === payout ? withAdjustments : [...platformFees];
  // Airbnb keeps the guest service fee and remits the taxes without listing
  // either as a deduction; name them when they are exactly what is missing.
  const paidOfType = (type: string) => sumCents(charges.filter((line) => line.type === type));
  const listedOfType = (type: string) => platformLines.some((line) => line.type === type);
  const serviceFee = { type: 'guest_service_fee', label: 'Guest service fee', cents: -paidOfType('guest_service_fee') };
  const taxes = { type: 'occupancy_tax', label: TAXES_REMITTED_LABELS[platform], cents: -paidOfType('occupancy_tax') };
  const unlisted = [serviceFee, taxes].filter((line) => line.cents && !listedOfType(line.type));
  const platformGap = payout - total - sumCents(platformLines);
  const named = [unlisted, unlisted.slice(0, 1), unlisted.slice(1)].find(
    (candidate) => candidate.length && sumCents(candidate) === platformGap
  );
  if (named) {
    platformLines.push(...named);
  } else if (platformGap) {
    platformLines.push({ type: '', label: 'Other adjustments', cents: platformGap });
  }
  const strip = ({ label, cents }: ChannelBreakdownLine) => ({ label, cents });
  return { guestLines: guestLines.map(strip), totalCents: total, platformLines: platformLines.map(strip), payoutCents: payout };
}

export interface ChannelBreakdownRow {
  label: string;
  value: string;
}

// The breakdown ready to render: guestRows add up to totalValue, and
// platformRows (titled platformTitle) take it down to payoutValue.
export interface ChannelBreakdownView {
  guestRows: ChannelBreakdownRow[];
  totalValue: string;
  platformTitle: string;
  platformRows: ChannelBreakdownRow[];
  payoutValue: string;
}

export function channelBreakdownView(
  sourceData: { amounts_breakdown?: unknown; money?: ChannelMoney | null } | null | undefined,
  platformName?: string | null,
  currency?: string | null
): ChannelBreakdownView | null {
  const breakdown = channelBreakdown(sourceData, platformName);
  if (!breakdown) {
    return null;
  }
  const rows = (lines: ChannelBreakdownLine[]) =>
    lines.map((line) => ({ label: line.label, value: formatCents(line.cents, currency) ?? '' }));
  return {
    guestRows: rows(breakdown.guestLines),
    totalValue: formatCents(breakdown.totalCents, currency) ?? '',
    platformTitle: KEPT_BY_LABELS[knownPlatform(platformName)],
    platformRows: rows(breakdown.platformLines),
    payoutValue: formatCents(breakdown.payoutCents, currency) ?? '',
  };
}

// The API takes a price of 0 (no price) or at least this many cents.
export const MINIMUM_RESERVATION_PRICE_CENTS = 100;

// The source type of a reservation booked through the host's TIDY listing.
export const TIDY_LISTING_SOURCE_SLUG = 'tidy';

const HOST_ADDED_SOURCE_FORMATS = ['app', 'csv'];

// Whether the host added the reservation themselves, through Add Reservation
// or a CSV upload. Only these take a price. Decided by the source type's slug
// and format, not its name: a CSV upload's type and the TIDY listing's are
// both named 'Tidy', and the TIDY listing's shares the app format.
export function isHostAddedSource(
  sourceType: { slug?: string | null; format?: string | null } | null | undefined
): boolean {
  return (
    HOST_ADDED_SOURCE_FORMATS.includes(sourceType?.format ?? '') &&
    sourceType?.slug !== TIDY_LISTING_SOURCE_SLUG
  );
}

export interface GuestReservationGuestLink {
  guest_id: number;
  is_booker?: boolean;
  is_staying?: boolean;
}

export interface CreateGuestReservation {
  address_id: number;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string;
  check_out_time?: string;
  details?: any;
  total_guests?: number;
  total_adults?: number;
  total_children?: number;
  total_infants?: number;
  total_pets?: number;
  // What the guest paid for the whole stay, in cents. Only reservations the
  // host adds themselves carry one; a synced reservation's money comes from
  // its integration.
  amount?: number | null;
  // Links existing guests (see CustomerGuest) to the reservation. Backend creates the
  // guest_reservation_guests rows. Shape matches gotidy's `guests` array param on
  // POST /api/v1/customer/guest-reservations.
  guests?: GuestReservationGuestLink[];
}

export interface UpdateGuestReservation {
  guest_reservation_id: number;
  check_in_date?: string;
  check_out_date?: string;
  check_in_time?: string;
  check_out_time?: string;
  details?: string;
  // Total stay price in cents; null or 0 clears it, and leaving it out keeps it.
  amount?: number | null;
}
